import { useState } from 'react'

import type { ReportReason, UserBrief } from '@/api/types'
import { REPORT_REASONS } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useBlockToggle, useMuteToggle } from '@/features/relations'

import { useReport } from '../hooks/useModeration'
import { REASON_TEXT } from '../reasons'

const NOTE_MAX = 500

interface Props {
  open: boolean
  onClose: () => void
  /** 被举报的人：举报帖子时是帖子作者 */
  target: UserBrief
  /** 给了就是举报这条帖子，没给就是举报这个人 */
  postId?: string
  /** 已知的静音 / 屏蔽状态；提交后的快捷按钮据此决定显示与否 */
  muting?: boolean
  blocking?: boolean
}

/**
 * 举报。选理由 → 可选说明 → 提交。
 *
 * 提交后如实说明「只进人工审核，不会自动隐藏」（D67），并顺手给出静音 / 屏蔽：
 * 很多举报其实只是「我不想再看到他」，那用静音就够了。
 */
export function ReportDialog({
  open,
  onClose,
  target,
  postId,
  muting = false,
  blocking = false,
}: Props) {
  const t = useT()
  const report = useReport()
  const mute = useMuteToggle()
  const block = useBlockToggle()
  const [reason, setReason] = useState<ReportReason | null>(null)
  const [note, setNote] = useState('')

  const title = postId
    ? t('report.titlePost')
    : t('report.titleUser', { username: target.username })

  const submit = () => {
    if (!reason) return
    report.mutate({
      reason,
      note: note.trim() || undefined,
      ...(postId ? { post_id: postId } : { username: target.username }),
    })
  }

  if (report.isSuccess) {
    return (
      <Dialog open={open} onClose={onClose} title={t('report.doneTitle')}>
        <div className="space-y-4 px-4 pb-5">
          <p className="text-[15px] text-ink-muted">{t('report.doneBody')}</p>
          <div className="flex flex-wrap gap-2">
            {!muting && (
              <Button
                variant="outline"
                loading={mute.isPending}
                disabled={mute.isSuccess}
                onClick={() => mute.mutate({ username: target.username, muting: false })}
              >
                {t('report.alsoMute', { username: target.username })}
              </Button>
            )}
            {!blocking && (
              <Button
                variant="outline"
                className="text-danger"
                loading={block.isPending}
                disabled={block.isSuccess}
                onClick={() => block.mutate({ username: target.username, blocking: false })}
              >
                {t('report.alsoBlock', { username: target.username })}
              </Button>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose}>{t('report.done')}</Button>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="pb-5">
        <p className="px-4 pb-2 text-[15px] font-bold">{t('report.question')}</p>
        <div role="radiogroup" aria-label={t('report.question')}>
          {REPORT_REASONS.map((value) => {
            const active = reason === value
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setReason(value)}
                className={cn(
                  'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-hover',
                  active && 'bg-surface-subtle',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-1 size-4 shrink-0 rounded-full border-2',
                    active ? 'border-brand-ink bg-brand-ink' : 'border-line-strong',
                  )}
                />
                <span>
                  <span className="block text-[15px] font-bold">{t(REASON_TEXT[value].label)}</span>
                  <span className="block text-[13px] text-ink-muted">
                    {t(REASON_TEXT[value].description)}
                  </span>
                </span>
              </button>
            )
          })}
        </div>

        <label className="mt-3 block px-4">
          <span className="text-[13px] font-bold text-ink-muted">{t('report.note')}</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
            placeholder={t('report.notePlaceholder')}
            rows={3}
            className="mt-1 w-full resize-none rounded-md border border-line-strong bg-transparent px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
          />
        </label>

        {report.isError && (
          <p role="alert" className="px-4 pt-2 text-sm text-danger">
            {t('report.failed')}
          </p>
        )}

        <div className="flex justify-end px-4 pt-3">
          <Button onClick={submit} disabled={!reason} loading={report.isPending}>
            {t('report.submit')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
